import { RestError } from '@azure/core-http';
import { useMemo } from 'react';
import { ProjectPathParams, Paths } from '../../../app/router/app.router';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { ListPageHeader } from '../../../ui/components/pure/page-headers/list/list-page-header.component';
import { WindowTitle } from '../../../core/window-title/window-title.component';
import { UsersTable } from './components/users-list-table.component';
import { useUsersList } from './hooks/use-users-list.hook';
import { makeUsersListPageHeaderProps } from './logic/users-list-props.map';
import { useUserLabels } from '../../../hooks/use-entity-labels.hook';
import { UsersListActionsObserver } from './logic/user-list-actions-observer.component';
import { Container } from '@mui/material';

export const UsersListPage = ({ params }: { params: ProjectPathParams<Paths.customers> }) => {
  const {
    projectObject: project,
    result: customers,
    infiniteScroll,
    formData,
    setFormData,
    actions
  } = useUsersList(params.projectID);

  const sortData = useMemo(
    () => ({ sortBy: formData.sortBy, sortOrder: formData.sortOrder }),
    [formData.sortBy, formData.sortOrder]
  );
  const error = project.error || customers.error;

  const userLabels = useUserLabels(project.data?.id);

  if (error) {
    if ((error as RestError).statusCode === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" />;
    }

    return <ErrorFullPage />;
  }

  const headerProps = makeUsersListPageHeaderProps(
    customers.data?.pages[0].totalRecords || 0,
    formData,
    userLabels,
    setFormData,
    actions.setAction,
    params.projectID
  );

  return (
    <Container>
      {project.data && <WindowTitle detail={project.data.name} pageTitle={userLabels.plural} />}
      <ListPageHeader {...headerProps} />
      <UsersTable
        params={params}
        pages={customers.data?.pages || []}
        infiniteScroll={infiniteScroll}
        setFormData={setFormData}
        sortData={sortData}
        isLoading={customers.isLoading}
        isPreviousData={customers.isPreviousData}
        hasError={customers.isError}
      />
      <UsersListActionsObserver user={undefined} actions={actions} projectID={params.projectID} />
    </Container>
  );
};
